import { type ReactNode, lazy } from 'react';
import { type Root, createRoot } from 'react-dom/client';
import sConfigs from './_configs/_configs';
import { type ConfigObject, DebugLevel } from './_configs/types';
import { type HptSessionStorageSettings, LOCAL_STORAGE_SETTINGS, defaultSessionStorage } from './hooks/use-storage';
import './locales/i18n';
import modules from './modules/_definition/map';
import { type Modules } from './modules/_definition/types';
import ThemeProvider from './theme/index';
import { type AppContainer } from './types';
import checkUndefinedNullOrEmpty from './utils/tools/checkUndefinedNullOrEmpty';
import checkUndefinedOrNull from './utils/tools/checkUndefinedOrNull';

const Entry = lazy(() => import('./entry' /* webpackChunkName: "Entry" */));

let abortControllerFacets: AbortController;
let abortControllerFaaren: AbortController;
const reactContent: Partial<Record<Modules, Root>> = {};

const renderContainer = (rootContainer: HTMLDivElement, settings: ConfigObject, children: ReactNode) => (
    <ThemeProvider rootContainer={rootContainer} settings={settings}>
        {children}
    </ThemeProvider>
);

async function init() {
    let sessionStorage: HptSessionStorageSettings = typeof window !== 'undefined' ? JSON.parse(window?.sessionStorage?.getItem(LOCAL_STORAGE_SETTINGS) || '{}') : undefined;
    if (typeof window !== 'undefined' && checkUndefinedNullOrEmpty(sessionStorage)) {
        window?.sessionStorage?.setItem(LOCAL_STORAGE_SETTINGS, JSON.stringify(defaultSessionStorage));
        sessionStorage = defaultSessionStorage;
    }
    const settings = await sConfigs(undefined);
    if (sessionStorage?.debug === DebugLevel.CONFIG || sessionStorage?.debug >= DebugLevel.INFO) {
        console.log('index - settings', settings);
    }
    const containerIds: AppContainer[] = [];

    try {
        modules?.map((m) => {
            const rootContainer = document.getElementById(m?.t) as HTMLDivElement;
            if (!checkUndefinedOrNull(rootContainer)) {
                const Skeleton = <m.Skeleton rootContainer={rootContainer} settings={settings} />;
                const SkeletonDetails = !checkUndefinedOrNull(m.SkeletonDetails) ? <m.SkeletonDetails rootContainer={rootContainer} settings={settings} /> : '';
                containerIds.push({
                    id: m?.t,
                    allFaarenVehicles: undefined,
                    allFacets: undefined,
                    appProps: {
                        Component: m?.Component,
                        module: m?.i,
                    },
                    rootContainer,
                    settings,
                    Skeleton,
                    SkeletonDetails,
                });

                if (rootContainer?.hasChildNodes() === true) {
                    rootContainer.innerHTML = null;
                }
                reactContent[m?.i] = createRoot(rootContainer);
                reactContent?.[m?.i]?.render(renderContainer(rootContainer, settings, location?.search?.includes('_carId') || location?.pathname?.includes('/c/') ? SkeletonDetails : Skeleton));
            }
        });

        abortControllerFacets?.abort();
        abortControllerFacets = new AbortController();
        const getAllFacets = (await import('./_tools/axios/cars/getAllFacets' /* webpackChunkName: "getAllFacets" */))?.default;
        const allFacets = await getAllFacets(settings?.general?.idFirma, settings?.general?.language, settings?.general?.useLocationAlias, abortControllerFacets?.signal, settings?.general?.debug).then(function ({ data }) {
            return data?.facets || {};
        });
        abortControllerFacets = null;

        let allFaarenVehicles = [];
        if (settings?.api?.faaren?.enabled && !checkUndefinedNullOrEmpty(settings?.api?.faaren?.apiKey)) {
            abortControllerFaaren?.abort();
            abortControllerFaaren = new AbortController();
            const getFaarenVehicles = (await import('./_tools/axios/faaren/getFaarenVehicles' /* webpackChunkName: "getFaarenVehicles" */))?.default;
            allFaarenVehicles = await getFaarenVehicles(settings?.api?.faaren?.apiKey, undefined, abortControllerFaaren?.signal, settings?.general?.debug);
            abortControllerFaaren = null;
        }

        containerIds.map(function (container) {
            if (!checkUndefinedOrNull(container?.rootContainer)) {
                reactContent?.[container?.appProps?.module]?.render(
                    renderContainer(
                        container?.rootContainer,
                        container?.settings,
                        <Entry
                            allFacets={allFacets}
                            allFaarenVehicles={allFaarenVehicles}
                            appProps={container?.appProps}
                            id={container?.id}
                            rootContainer={container?.rootContainer}
                            settings={container?.settings}
                            Skeleton={container?.Skeleton}
                            SkeletonDetails={container?.SkeletonDetails}
                        />
                    )
                );
            }
        });
    } catch (e) {
        console.error('Failed to initialize:', e);
    }
}

init();
